import React, { useEffect, useRef } from 'react';
import { Assessment as AssessmentIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import InlineHtml from '../../../common/inline/InlineHtml';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';

export interface ChangeOrderProductCardrops extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const ChangeProductCardFields = [
  {
    name: 'fieldActionReview',
    label: 'Is this the result of field action?'
  },
  {
    name: 'hazardAssessmentReview',
    label: 'Reviewed/Updated Hazard Assessment?'
  },
  {
    name: 'designVerificationReview',
    label: 'Reviewed/Performed DVAL, DVER?'
  },
  {
    name: 'designReviewAttached',
    label: 'Attached/referenced design review or phase review meeting minutes?'
  },
  {
    name: 'changeCompatibility',
    label: 'Change compatibility (forward, backward)'
  },
  {
    name: 'manufacturingQualityImpact',
    label:
      'Impact on manufacturing, quality control (inspection) etc. If yes, answer "Manufacturing" change assessment questions.'
  },
  {
    name: 'changeSupplierImpact',
    label: 'Impact to supplier(s)'
  },
  {
    name: 'productRegulatoryImpact',
    label:
      'Does modification affect devices, components, labeling, or software that have received regulatory (FDA, EU NB, etc.) clearance/approval or are currently under review? If yes, complete Regulatory Impact Analysis'
  },
  {
    name: 'changeSoftwareModification',
    label:
      'Software modification? (If yes, complete a software validation analysis and, as needed, software regression testing)'
  },
  {
    name: 'dhfiUpdated',
    label: 'DHFI updated?'
  }
];

export const ChangeOrderProductCard: React.FC<
  ChangeOrderProductCardrops
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    fieldActionReview: changeOrder.fieldActionReview || '',
    hazardAssessmentReview: changeOrder.hazardAssessmentReview || '',
    designVerificationReview: changeOrder.designVerificationReview || '',
    designReviewAttached: changeOrder.designReviewAttached || '',
    changeCompatibility: changeOrder.changeCompatibility || '',
    manufacturingQualityImpact: changeOrder.manufacturingQualityImpact || '',
    changeSupplierImpact: changeOrder.changeSupplierImpact || '',
    productRegulatoryImpact: changeOrder.productRegulatoryImpact || '',
    changeSoftwareModification: changeOrder.changeSoftwareModification || '',
    dhfiUpdated: changeOrder.dhfiUpdated || '',
    commentsProduct: changeOrder.commentsProduct || '',
    productDueDate: changeOrder.productDueDate,
    productCompletedDate: changeOrder.productCompletedDate
  };
  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);
  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <AssessmentIcon />
          Product (Design/Software) Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {ChangeProductCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='productDueDate'
              label='Due Date'
              placeholder='Enter Due Date'
              disabled={isFieldDisabled('productDueDate')}
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='productCompletedDate'
              label='Completed Date'
              placeholder='Enter Completed Date'
              disabled={isFieldDisabled('productCompletedDate')}
              autoCloseOnSelect
            />
            <InlineHtml
              name='commentsProduct'
              label='Comments'
              disabled={isFieldDisabled('commentsProduct')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
